import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Search, X, Loader2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import {
  isGoogleMapsConfigured,
  loadGoogleMapsAPI,
  createSessionToken,
  getPlaceDetails
} from '../../utils/googleMapsLoader';

/**
 * LocationAutocomplete - Smart address search with Google Places / Nominatim fallback
 *
 * Features:
 * - Google Places Autocomplete when API key is configured
 * - Nominatim (OpenStreetMap) fallback when no API key
 * - Session tokens for Google cost optimization
 * - Debounced search
 * - Keyboard navigation
 * - Australia country restriction
 */
export default function LocationAutocomplete({
  value,
  onChange,
  onCoordinatesChange,
  placeholder = 'Search for address or business...',
  disabled = false,
  error = false,
  className
}) {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [useGoogle, setUseGoogle] = useState(false);
  const [googleLoaded, setGoogleLoaded] = useState(false);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const autocompleteServiceRef = useRef(null);

  // Initialize Google Maps if configured
  useEffect(() => {
    if (isGoogleMapsConfigured()) {
      loadGoogleMapsAPI()
        .then(() => {
          setUseGoogle(true);
          setGoogleLoaded(true);
          sessionTokenRef.current = createSessionToken();
          autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        })
        .catch(() => {
          setUseGoogle(false);
        });
    }
  }, []);

  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || '');
    }
  }, [value]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        !inputRef.current?.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search using Google Places Autocomplete
  const searchGoogle = useCallback(async (query) => {
    if (!autocompleteServiceRef.current || !query || query.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);

    try {
      const request = {
        input: query,
        componentRestrictions: { country: 'au' },
        sessionToken: sessionTokenRef.current,
        types: ['geocode', 'establishment']
      };

      autocompleteServiceRef.current.getPlacePredictions(request, (predictions, status) => {
        setIsSearching(false);

        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(
            predictions.map((p) => ({
              id: p.place_id,
              address: p.description,
              mainText: p.structured_formatting?.main_text || p.description,
              secondaryText: p.structured_formatting?.secondary_text || '',
              placeId: p.place_id,
              isGoogle: true
            }))
          );
        } else {
          setSuggestions([]);
        }
      });
    } catch (err) {
      console.error('Google Places search error:', err);
      setIsSearching(false);
      setSuggestions([]);
    }
  }, []);

  // Search using Nominatim (OpenStreetMap)
  const searchNominatim = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=au&limit=5&addressdetails=1`,
        {
          headers: {
            'Accept-Language': 'en-AU'
          }
        }
      );

      const data = await response.json();

      setSuggestions(
        data?.map((item, index) => ({
          id: `nominatim-${index}`,
          address: item.display_name,
          mainText: item.name || item.display_name?.split(',')[0] || '',
          secondaryText: item.display_name?.split(',').slice(1).join(',').trim() || '',
          lat: parseFloat(item.lat),
          lng: parseFloat(item.lon),
          isGoogle: false
        })) || []
      );
    } catch (err) {
      console.error('Nominatim search error:', err);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search handler
  const handleSearch = useCallback(
    (query) => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      if (!query || query.length < 2) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setShowSuggestions(true);

      searchTimeoutRef.current = setTimeout(
        () => {
          if (useGoogle && googleLoaded) {
            searchGoogle(query);
          } else {
            searchNominatim(query);
          }
        },
        useGoogle ? 300 : 500 // Faster debounce for Google
      );
    },
    [useGoogle, googleLoaded, searchGoogle, searchNominatim]
  );

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
    handleSearch(newValue);
    setHighlightedIndex(-1);
  };

  // Handle suggestion selection
  const handleSelectSuggestion = async (suggestion) => {
    setInputValue(suggestion.address);
    onChange?.(suggestion.address);
    setSuggestions([]);
    setShowSuggestions(false);

    // Get coordinates
    if (suggestion.isGoogle && suggestion.placeId) {
      try {
        // Create new session token for next search
        const currentToken = sessionTokenRef.current;
        sessionTokenRef.current = createSessionToken();

        const place = await getPlaceDetails(suggestion.placeId, currentToken);
        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();

        if (lat && lng) {
          onCoordinatesChange?.({
            lat,
            lng,
            address: place.formatted_address || suggestion.address,
            placeId: suggestion.placeId
          });
        }
      } catch (err) {
        console.error('Failed to get place details:', err);
        // Fallback: just use the address without coordinates
        onCoordinatesChange?.({
          lat: null,
          lng: null,
          address: suggestion.address,
          placeId: suggestion.placeId
        });
      }
    } else if (suggestion.lat && suggestion.lng) {
      // Nominatim already has coordinates
      onCoordinatesChange?.({
        lat: suggestion.lat,
        lng: suggestion.lng,
        address: suggestion.address,
        placeId: null
      });
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Handle clear
  const handleClear = () => {
    setInputValue('');
    onChange?.('');
    onCoordinatesChange?.(null);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  return (
    <div className={cn('relative', className)}>
      {/* Input Field */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white-shade-2" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue && suggestions.length > 0 && setShowSuggestions(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full pl-10 pr-10 py-2 bg-white border rounded-lg text-background placeholder-gray-500',
            'focus:outline-none focus:border-primary-orange transition-colors',
            error ? 'border-red-500' : 'border-gray-300',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        />

        {/* Loading / Clear buttons */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isSearching && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
          {inputValue && !disabled && !isSearching && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.id}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={cn(
                'w-full px-4 py-3 text-left transition-colors',
                'first:rounded-t-lg last:rounded-b-lg',
                index === highlightedIndex ? 'bg-orange-50' : 'hover:bg-gray-50'
              )}
            >
              <div className="flex items-start gap-3">
                <MapPin
                  className={cn(
                    'w-4 h-4 mt-0.5 flex-shrink-0',
                    index === highlightedIndex ? 'text-primary-orange' : 'text-gray-400'
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      'text-sm font-medium truncate',
                      index === highlightedIndex ? 'text-primary-orange' : 'text-gray-900'
                    )}
                  >
                    {suggestion.mainText}
                  </p>
                  {suggestion.secondaryText && (
                    <p className="text-xs text-gray-500 truncate mt-0.5">
                      {suggestion.secondaryText}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}

          {/* Provider attribution */}
          <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
            <p className="text-[10px] text-gray-400 text-right">
              {useGoogle ? 'Powered by Google' : 'Powered by OpenStreetMap'}
            </p>
          </div>
        </div>
      )}

      {/* No results message */}
      {showSuggestions && !isSearching && inputValue.length >= 3 && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
          <p className="text-sm text-gray-500 text-center">No addresses found</p>
        </div>
      )}
    </div>
  );
}
